import React, { useState, useEffect, useCallback, lazy, Suspense, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, 
  CircularProgress, Alert, Grid, Chip,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Snackbar, Container, Card, CardContent, List, ListItem, ListItemText,
  ListItemIcon, Divider, Tabs, Tab, IconButton, Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import TopNav from './TopNav';
import Fonts from './Fonts';
import { getUnmoderatedPubs, approveGarden, getPubByGooglePlaceId, getPubRaycasting } from '../API';
import { themeColors } from '../styles/theme';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { latLonElevToECEF } from '../API';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GrassIcon from '@mui/icons-material/Grass';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Lazy load the 3D viewer component to avoid loading its resources until needed
const ShadedVertexViewer = lazy(() => import('./ViewSunlight3D'));

// Types
interface Pub {
  name: string;
  uid: string;
  address?: string;
  latitude: number;
  longitude: number;
  garden_drawn: boolean;
  garden_moderated: boolean;
  date_added?: string;
  sub_areas: SubArea[];
  google_place_id?: string;
  city?: string;
}

interface SubArea {
  uid: string;
  name: string;
  polygon: [number, number][];
  is_uncertain?: boolean;
}

interface RaycastingData {
  mesh_uris?: string[];
  centre_ecef?: [number, number, number];
  garden_details?: Record<string, { name: string; is_uncertain: boolean }>;
  session_id?: string;
  sun_vector?: { [timestamp: string]: [number, number, number] };
  [key: string]: any;
}

// Styled components
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '12px',
  padding: '10px 20px',
}));

const PrimaryButton = styled(StyledButton)(({ theme }) => ({
  background: themeColors.orange,
  color: '#fff',
  boxShadow: '0 4px 10px rgba(252, 82, 8, 0.3)',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(90deg, ${themeColors.orange} 0%, ${themeColors.mainYellow} 100%)`,
    boxShadow: '0 6px 14px rgba(252, 82, 8, 0.4)',
  },
  '&:disabled': {
    background: '#E0E0E0',
    color: '#9E9E9E',
    boxShadow: 'none',
  },
}));

const ActionButton = styled(StyledButton)(({ theme }) => ({
  color: themeColors.darkBackground,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderWidth: 2,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    borderWidth: 2,
  },
}));

const DangerButton = styled(StyledButton)(({ theme }) => ({
  backgroundColor: '#f44336',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#d32f2f',
  },
}));

const ApproveButton = styled(StyledButton)(({ theme }) => ({
  backgroundColor: '#4caf50',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#388e3c',
  },
}));

const GardenListItem = styled(ListItem)<{ selected?: boolean; uncertain?: boolean }>(({ selected, uncertain, theme }) => ({
  borderRadius: '8px',
  marginBottom: '8px',
  backgroundColor: selected 
    ? 'rgba(252, 82, 8, 0.1)' 
    : uncertain 
      ? 'rgba(255, 235, 59, 0.1)' 
      : 'rgba(255, 255, 255, 0.8)',
  borderLeft: selected 
    ? `4px solid ${themeColors.orange}` 
    : uncertain 
      ? '4px solid #FFD600' 
      : '4px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: selected 
      ? 'rgba(252, 82, 8, 0.15)' 
      : uncertain 
        ? 'rgba(255, 235, 59, 0.15)' 
        : 'rgba(255, 255, 255, 0.9)',
    cursor: 'pointer',
  },
}));

const ViewerContainer = styled(Box)(({ theme }) => ({
  height: '500px',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
}));

const ModerationAdmin: React.FC = () => {
  // State for pubs and pagination
  const [unmoderatedPubs, setUnmoderatedPubs] = useState<Pub[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [totalPubs, setTotalPubs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [processedMeshUrls, setProcessedMeshUrls] = useState<string[]>([]);

  
  // State for currently selected pub and garden
  const [currentPubIndex, setCurrentPubIndex] = useState(0);
  const [selectedPub, setSelectedPub] = useState<Pub | null>(null);
  const [selectedGardenIndex, setSelectedGardenIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  
  // State for raycast data and mesh URIs
  const [raycastingData, setRaycastingData] = useState<RaycastingData | null>(null);
  const [meshUris, setMeshUris] = useState<string[]>([]);
  const [focalPoint, setFocalPoint] = useState<[number, number, number] | undefined>();
  const [sessionId, setSessionId] = useState<string | undefined>();
  
  // UI state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  const navigate = useNavigate();
  
  // Get the selected garden based on selected pub and garden index
  const selectedGarden = selectedPub && selectedPub.sub_areas.length > selectedGardenIndex
    ? selectedPub.sub_areas[selectedGardenIndex]
    : null;
  
  // Load unmoderated pubs on component mount
  useEffect(() => {
    fetchUnmoderatedPubs();
  }, [currentPage]);
  
  // Set the first pub as selected when data loads
  useEffect(() => {
    if (unmoderatedPubs.length > 0 && !selectedPub) {
      selectPub(unmoderatedPubs[0], 0);
    }
  }, [unmoderatedPubs]);
  
  // Load raycasting data when selected pub changes
  useEffect(() => {
    if (selectedPub) {
      fetchRaycastingData();
    }
  }, [selectedPub, selectedGardenIndex]);
  
  // Fetch unmoderated pubs with pagination
  const fetchUnmoderatedPubs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getUnmoderatedPubs(currentPage);
      
      if (response.data && response.data.pubs) {
        setUnmoderatedPubs(response.data.pubs);
        setTotalPubs(response.data.total_count);
        setTotalPages(response.data.total_pages);
        
        // Show a message if no pubs need moderation
        if (response.data.pubs.length === 0) {
          setError('No pubs currently need moderation. Great job!');
        }
      }
    } catch (err: any) {
      setError('Failed to load unmoderated pubs. Please refresh and try again.');
      console.error('Error fetching unmoderated pubs:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch raycasting data for 3D visualization
  const fetchRaycastingData = async () => {
    if (!selectedPub) return;
    
    try {
        setViewerLoading(true);
        
        const response = await getPubRaycasting(selectedPub.uid, true);
        if (response.data) {
            setRaycastingData(response.data);
            
            // Store raw mesh URIs
            if (response.data.mesh_uris && response.data.mesh_uris.length > 0) {
                setMeshUris(response.data.mesh_uris);
                
                // Process the mesh URIs into full URLs with authentication
                const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                const baseUrl = 'https://tile.googleapis.com';
                const sessionId = response.data.session_id || '';
                
                // Create complete URLs
                const completeUrls = response.data.mesh_uris.map((uri: string) => {
                    const uriPath = uri.startsWith('/') ? uri : `/${uri}`;
                    const cleanSessionId = sessionId.startsWith('session=') ? 
                        sessionId.substring(8) : sessionId;
                    return `${baseUrl}${uriPath}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
                });
                
                setProcessedMeshUrls(completeUrls);
            }
            
            if (response.data.centre_ecef) {
                setFocalPoint(response.data.centre_ecef as [number, number, number]);
            }
            
            if (response.data.session_id) {
                setSessionId(response.data.session_id);
            }
        }
    } catch (err) {
        console.error('Error fetching raycasting data:', err);
        setSnackbarMessage('Failed to load 3D visualization data');
        setSnackbarOpen(true);
    } finally {
        setViewerLoading(false);
    }
};

const processGardenData = useMemo(() => {
  if (!selectedPub || !selectedGarden || !raycastingData) {
      return [];
  }
  
  // Find this garden's data in the raycasting response
  const gardenId = selectedGarden.uid;
  const gardenData = raycastingData[gardenId];
  
  if (!gardenData || !gardenData.garden_vertex_coords) {
      return [];
  }
  
  // Process the coordinates
  const coords = gardenData.garden_vertex_coords.map((coord: number[]) => {
      if (Array.isArray(coord) && coord.length >= 3) {
          const [lat, lon, elevation] = coord;
          return latLonElevToECEF(lat, lon, elevation);
      }
      return null;
  }).filter((coord: any): coord is [number, number, number] => coord !== null);
  
  if (coords.length === 0) {
      return [];
  }
  
  // Get the raycast results
  const hourlyData = gardenData.hourly_data || {};
  
  return [{
      uid: gardenId,
      name: selectedGarden.name,
      ecefCoordinates: coords,
      raycastResults: hourlyData
  }];
}, [selectedPub, selectedGarden, raycastingData]);
  
  // Select a pub for review and initialize with the first garden
  const selectPub = (pub: Pub, pubIndex: number) => {
    setSelectedPub(pub);
    setCurrentPubIndex(pubIndex);
    setSelectedGardenIndex(0);
    setActiveTab(0);
    
    // Reset raycasting data
    setRaycastingData(null);
    setMeshUris([]);
    setFocalPoint(undefined);
    setSessionId(undefined);
  };
  
  // Select a specific garden in the current pub
  const selectGarden = (gardenIndex: number) => {
    if (selectedPub && gardenIndex < selectedPub.sub_areas.length) {
      setSelectedGardenIndex(gardenIndex);
    }
  };
  
  // Handle garden approval
  const handleApprove = () => {
    if (!selectedPub) return;
    setApproveDialogOpen(true);
  };
  
  // Confirm garden approval
  const confirmApproval = async () => {
    if (!selectedPub) return;
    
    try {
      setLoading(true);
      const response = await approveGarden(selectedPub.uid);
      
      if (response.status === 200) {
        setSnackbarMessage(`"${selectedPub.name}" approved successfully!`);
        setSnackbarOpen(true);
        
        // Remove approved pub from list
        const updatedPubs = unmoderatedPubs.filter(p => p.uid !== selectedPub.uid);
        setUnmoderatedPubs(updatedPubs);
        
        // Select next pub or go to next page if needed
        if (updatedPubs.length > 0) {
          // If there are still pubs on this page, select the next one
          let nextIndex = currentPubIndex;
          if (nextIndex >= updatedPubs.length) {
            nextIndex = 0;
          }
          setCurrentPubIndex(nextIndex);
          selectPub(updatedPubs[nextIndex], nextIndex);
        } else {
          // If all pubs on current page are processed, go to next page or back to first
          if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
          } else if (totalPages > 0) {
            setCurrentPage(1);
          } else {
            // No pubs left to moderate
            setSelectedPub(null);
            setError('All pubs have been moderated. Great job!');
          }
        }
      }
    } catch (err) {
      console.error('Error approving garden:', err);
      setSnackbarMessage('Failed to approve garden');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setApproveDialogOpen(false);
    }
  };
  
  // Handle garden rejection/deletion
  const handleReject = () => {
    if (!selectedPub || !selectedGarden) return;
    setRejectDialogOpen(true);
  };
  
  // Confirm garden rejection (deletion)
  const confirmRejection = async () => {
    if (!selectedPub || !selectedGarden) return;
    
    try {
      setLoading(true);
      
      // Call the delete garden API
      const response = await axios.delete(`${config.apiBaseUrl}delete_garden/`, {
        data: { uid: selectedGarden.uid }
      });
      
      if (response.status === 200) {
        setSnackbarMessage(`Garden "${selectedGarden.name}" deleted successfully!`);
        setSnackbarOpen(true);
        
        // Update the local state to remove the deleted garden
        const updatedPub = { ...selectedPub };
        updatedPub.sub_areas = updatedPub.sub_areas.filter(g => g.uid !== selectedGarden.uid);
        
        const updatedPubs = [...unmoderatedPubs];
        updatedPubs[currentPubIndex] = updatedPub;
        setUnmoderatedPubs(updatedPubs);
        
        // If this was the last garden, remove the pub from the list
        if (updatedPub.sub_areas.length === 0) {
          const filteredPubs = updatedPubs.filter(p => p.uid !== selectedPub.uid);
          setUnmoderatedPubs(filteredPubs);
          
          if (filteredPubs.length > 0) {
            let nextIndex = currentPubIndex;
            if (nextIndex >= filteredPubs.length) {
              nextIndex = 0;
            }
            selectPub(filteredPubs[nextIndex], nextIndex);
          } else {
            // If no more pubs, go to next page or show completion
            if (currentPage < totalPages) {
              setCurrentPage(currentPage + 1);
            } else {
              setSelectedPub(null);
              setError('All pubs have been moderated. Great job!');
            }
          }
        } else {
          // If there are still gardens, select the next one or the previous one
          let nextIndex = selectedGardenIndex;
          if (nextIndex >= updatedPub.sub_areas.length) {
            nextIndex = updatedPub.sub_areas.length - 1;
          }
          setSelectedGardenIndex(nextIndex);
          setSelectedPub(updatedPub);
        }
      }
    } catch (err) {
      console.error('Error deleting garden:', err);
      setSnackbarMessage('Failed to delete garden');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setRejectDialogOpen(false);
    }
  };
  
  // Navigate to edit page
  const handleEditGarden = () => {
    if (!selectedPub || !selectedGarden) return;
    
    // Navigate to SubmitGarden3D with the garden data
    navigate(`/submit`, {
      state: {
        selectedPub: {
          ...selectedPub,
          google_place_id: selectedPub.uid // Ensure we have the correct ID format
        },
        gardenToEdit: selectedGarden,
        returnToModeration: true
      }
    });
  };
  
  // Navigate to next/previous pub
  const handleNextPub = useCallback(() => {
    if (unmoderatedPubs.length === 0) return;
    
    let nextIndex = (currentPubIndex + 1) % unmoderatedPubs.length;
    selectPub(unmoderatedPubs[nextIndex], nextIndex);
  }, [unmoderatedPubs, currentPubIndex]);
  
  const handlePreviousPub = useCallback(() => {
    if (unmoderatedPubs.length === 0) return;
    
    let prevIndex = (currentPubIndex - 1 + unmoderatedPubs.length) % unmoderatedPubs.length;
    selectPub(unmoderatedPubs[prevIndex], prevIndex);
  }, [unmoderatedPubs, currentPubIndex]);
  
  // Navigate to next/previous garden
  const handleNextGarden = useCallback(() => {
    if (!selectedPub || selectedPub.sub_areas.length <= 1) return;
    
    let nextIndex = (selectedGardenIndex + 1) % selectedPub.sub_areas.length;
    selectGarden(nextIndex);
  }, [selectedPub, selectedGardenIndex]);
  
  const handlePreviousGarden = useCallback(() => {
    if (!selectedPub || selectedPub.sub_areas.length <= 1) return;
    
    let prevIndex = (selectedGardenIndex - 1 + selectedPub.sub_areas.length) % selectedPub.sub_areas.length;
    selectGarden(prevIndex);
  }, [selectedPub, selectedGardenIndex]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Logout from admin
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  // Check if garden is uncertain
  const isGardenUncertain = (garden: SubArea) => {
    return garden.is_uncertain === true;
  };
  
  // Skip this pub and move to the next one
  const handleSkipPub = () => {
    handleNextPub();
    setSnackbarMessage('Skipped to next pub');
    setSnackbarOpen(true);
  };
  
  // Create help dialog content
  const getHelpContent = () => (
    <>
      <Typography variant="h6" gutterBottom>
        Moderation Guide
      </Typography>
      <Typography variant="body2" paragraph>
        This moderation panel allows you to review and approve garden submissions from users.
        Here's how to use it:
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
        Main Actions
      </Typography>
      <List dense>
        <ListItem>
          <ListItemIcon><ThumbUpAltIcon color="success" /></ListItemIcon>
          <ListItemText primary="Approve Garden" secondary="Approve the garden if it looks accurate and appropriate." />
        </ListItem>
        <ListItem>
          <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
          <ListItemText primary="Edit Garden" secondary="Make changes to the garden shape or details before approving." />
        </ListItem>
        <ListItem>
          <ListItemIcon><CloseIcon color="error" /></ListItemIcon>
          <ListItemText primary="Reject Garden" secondary="Delete inappropriate or inaccurate garden submissions." />
        </ListItem>
        <ListItem>
          <ListItemIcon><SkipNextIcon /></ListItemIcon>
          <ListItemText primary="Skip" secondary="Move to the next pub without taking action." />
        </ListItem>
      </List>
      
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
        Navigation
      </Typography>
      <List dense>
        <ListItem>
          <ListItemIcon><ArrowBackIcon /></ListItemIcon>
          <ListItemText primary="Previous" secondary="Go to the previous pub or garden." />
        </ListItem>
        <ListItem>
          <ListItemIcon><ArrowForwardIcon /></ListItemIcon>
          <ListItemText primary="Next" secondary="Go to the next pub or garden." />
        </ListItem>
      </List>
      
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
        What to Check
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText primary="Garden Shape" secondary="Does the garden shape match the visible area in the 3D view?" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Garden Name" secondary="Is the garden name appropriate and descriptive?" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Uncertain Markers" secondary="Gardens marked with a yellow border are flagged as 'uncertain' by the submitter." />
        </ListItem>
      </List>
    </>
  );
  
  // Return the component JSX
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Fonts />
      <TopNav title="Garden Moderation" />
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 4, mt: '14vh', pt: 4 }}>
        <Paper sx={{ p: 3, mb: 4, borderRadius: '16px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Garden Moderation Panel
              <IconButton 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }}
                onClick={() => setHelpDialogOpen(true)}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Typography>
            
            <DangerButton
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </DangerButton>
          </Box>
          
          <Typography sx={{ mb: 3, color: 'text.secondary' }}>
            Review and approve garden submissions. Make any necessary edits before approving.
          </Typography>
          
          {loading && !selectedPub && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress sx={{ color: themeColors.orange }} />
            </Box>
          )}
          
          {error && !loading && !selectedPub && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {!loading && !error && unmoderatedPubs.length > 0 && (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                  {selectedPub ? (
                    <>
                      Reviewing: <strong>{selectedPub.name}</strong>
                      <Typography component="span" sx={{ ml: 2, color: 'text.secondary' }}>
                        ({currentPubIndex + 1} of {unmoderatedPubs.length})
                      </Typography>
                    </>
                  ) : (
                    'Select a pub to review'
                  )}
                </Typography>
                
                {selectedPub && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      icon={<LocationOnIcon />}
                      label={selectedPub.address || 'No address available'}
                      variant="outlined"
                    />
                    <Chip
                      icon={<GrassIcon />}
                      label={`${selectedPub.sub_areas.length} Garden${selectedPub.sub_areas.length !== 1 ? 's' : ''}`}
                      color="primary"
                    />
                    {selectedGarden && (
                      <Chip
                        label={`Selected: ${selectedGarden.name}`}
                        color="secondary"
                        variant={isGardenUncertain(selectedGarden) ? "outlined" : "filled"}
                        sx={isGardenUncertain(selectedGarden) ? { 
                          border: '2px solid #FFD600',
                          color: '#B7940D'
                        } : {}}
                      />
                    )}
                    {selectedGarden && isGardenUncertain(selectedGarden) && (
                      <Chip
                        icon={<InfoIcon />}
                        label="Submitter marked as uncertain"
                        sx={{ 
                          backgroundColor: 'rgba(255, 235, 59, 0.2)',
                          color: '#B7940D',
                          border: '1px solid #FFD600'
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                >
                  <Tab label="3D View" />
                  <Tab label="Garden List" />
                  <Tab label="Pub Details" />
                </Tabs>
              </Box>
              
              {/* Tab Panel: 3D View */}
              {activeTab === 0 && (
                <ViewerContainer>
                  {viewerLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress sx={{ color: themeColors.orange }} thickness={4} size={60} />
                    </Box>
                  ) : !meshUris.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                      <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Loading 3D data...
                      </Typography>
                    </Box>
                  ) : (
                    <Suspense fallback={
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress sx={{ color: themeColors.orange }} />
                      </Box>
                    }>
                      <ShadedVertexViewer
                          meshUris={processedMeshUrls} // Use the processed URLs instead of raw mesh URIs
                          initialZoom={30}
                          focalPoint={focalPoint}
                          //markerSize={0.25}
                          gardenData={processGardenData} // Use the processed garden data
                          sunVectors={raycastingData?.sun_vector}
                          height="100%"
                          //skipRaycasting={true}
                      />
                    </Suspense>
                  )}
                </ViewerContainer>
              )}
              
              {/* Tab Panel: Garden List */}
              {activeTab === 1 && (
                <Box sx={{ height: '500px', overflowY: 'auto', p: 2, backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: '12px' }}>
                  <Typography variant="h6" gutterBottom>
                    Gardens for {selectedPub?.name}
                  </Typography>
                  <List>
                    {selectedPub?.sub_areas.map((garden, index) => (
                      <GardenListItem 
                        key={garden.uid}
                        selected={index === selectedGardenIndex}
                        uncertain={isGardenUncertain(garden)}
                        onClick={() => selectGarden(index)}
                      >
                        <ListItemIcon>
                          <GrassIcon color={isGardenUncertain(garden) ? "warning" : "primary"} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={garden.name}
                          secondary={`${garden.polygon.length} vertices ${isGardenUncertain(garden) ? '• Marked as uncertain' : ''}`}
                        />
                      </GardenListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {/* Tab Panel: Pub Details */}
              {activeTab === 2 && (
                <Box sx={{ height: '500px', overflowY: 'auto', p: 2, backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: '12px' }}>
                  <Typography variant="h6" gutterBottom>
                    Pub Information
                  </Typography>
                  
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {selectedPub?.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Address:</strong> {selectedPub?.address || 'Not available'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>City:</strong> {selectedPub?.city || 'Not available'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Google Place ID:</strong> {selectedPub?.google_place_id || selectedPub?.uid || 'Not available'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Date Added:</strong> {selectedPub?.date_added 
                          ? new Date(selectedPub.date_added).toLocaleDateString() 
                          : 'Not available'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Coordinates:</strong> {selectedPub 
                          ? `${selectedPub.latitude.toFixed(6)}, ${selectedPub.longitude.toFixed(6)}` 
                          : 'Not available'}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Gardens:</strong> {selectedPub?.sub_areas.length || 0}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Garden Drawn:</strong> {selectedPub?.garden_drawn ? 'Yes' : 'No'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Garden Moderated:</strong> {selectedPub?.garden_moderated ? 'Yes' : 'No'}
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<InfoIcon />}
                    onClick={() => window.open(`/pub/${selectedPub?.uid}`, '_blank')}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    View Pub Page
                  </Button>
                </Box>
              )}
              
              <Grid container spacing={2} sx={{ mt: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <ActionButton
                      variant="outlined"
                      onClick={selectedPub?.sub_areas.length && selectedPub.sub_areas.length > 1 ? handlePreviousGarden : handlePreviousPub}
                      startIcon={<ArrowBackIcon />}
                      disabled={loading || (unmoderatedPubs.length <= 1 && (!selectedPub || selectedPub.sub_areas.length <= 1))}
                    >
                      {selectedPub?.sub_areas.length && selectedPub.sub_areas.length > 1 ? "Previous Garden" : "Previous Pub"}
                    </ActionButton>
                    
                    <ActionButton
                      variant="outlined"
                      onClick={selectedPub?.sub_areas.length && selectedPub.sub_areas.length > 1 ? handleNextGarden : handleNextPub}
                      endIcon={<ArrowForwardIcon />}
                      disabled={loading || (unmoderatedPubs.length <= 1 && (!selectedPub || selectedPub.sub_areas.length <= 1))}
                    >
                      {selectedPub?.sub_areas.length && selectedPub.sub_areas.length > 1 ? "Next Garden" : "Next Pub"}
                    </ActionButton>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <ActionButton
                      variant="outlined"
                      onClick={handleSkipPub}
                      startIcon={<SkipNextIcon />}
                      disabled={loading || unmoderatedPubs.length <= 1}
                    >
                      Skip
                    </ActionButton>
                    
                    <DangerButton
                      variant="contained"
                      onClick={handleReject}
                      disabled={loading || !selectedGarden}
                      startIcon={<CloseIcon />}
                    >
                      Reject
                    </DangerButton>
                    
                    <ActionButton
                      variant="outlined"
                      onClick={handleEditGarden}
                      disabled={loading || !selectedGarden}
                      startIcon={<EditIcon />}
                    >
                      Edit
                    </ActionButton>
                    
                    <ApproveButton
                      variant="contained"
                      onClick={handleApprove}
                      disabled={loading || !selectedPub}
                      startIcon={<ThumbUpAltIcon />}
                    >
                      Approve
                    </ApproveButton>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </Container>
      
      {/* Approval Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve all gardens for "{selectedPub?.name}"? 
            This will mark the pub as moderated and make it visible to all users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmApproval} color="primary" variant="contained">
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rejection Confirmation Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject and delete the garden "{selectedGarden?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmRejection} color="error" variant="contained">
            Delete Garden
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Garden Moderation Help</DialogTitle>
        <DialogContent>
          {getHelpContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} color="primary" variant="contained">
            Got It
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ModerationAdmin;